/**
 * Shared role-matching utility for guided tour flows.
 *
 * Used by the client-safe registry, the server-side API route, and the
 * client-side useTourConfig hook to avoid logic duplication.
 */

import type { TourFlow } from './types';

/**
 * Determine if a flow is accessible for the given role.
 *
 * - If `role` is falsy or `'all'`, the flow is always accessible.
 * - If the flow has no `roles` array (or an empty one), it is accessible
 *   to every role.
 * - Otherwise the flow is accessible if its `roles` array contains the
 *   normalised role or the wildcard `'all'`.
 */
export function flowMatchesRole(flow: TourFlow, role?: string | null): boolean {
  if (!role || role === 'all') {
    return true;
  }

  const normalizedRole = role.toLowerCase().trim();

  if (!flow.roles || flow.roles.length === 0) {
    return true;
  }

  return flow.roles.some(
    (r) => r.toLowerCase().trim() === normalizedRole || r.toLowerCase().trim() === 'all'
  );
}
